'use client';

import React, { useMemo } from 'react';
import * as d3 from 'd3';

export interface LineChartLine {
  label: string;
  color: string;
  data: { x: string; y: number }[];
}

interface MedLineChartProps {
  lines: LineChartLine[];
  xLabels: string[];
  formatY?: (value: number) => string;
}

const width = 600;
const height = 200;
const margin = { top: 12, right: 24, bottom: 24, left: 28 };

const defaultFormatY = (value: number): string => {
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return String(value);
};

const MedLineChart: React.FC<MedLineChartProps> = ({
  lines,
  xLabels,
  formatY = defaultFormatY,
}) => {
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const xScale = useMemo(
    () => d3.scalePoint().domain(xLabels).range([0, innerWidth]),
    [xLabels, innerWidth]
  );

  const yMax = useMemo(
    () => d3.max(lines.flatMap((l) => l.data.map((d) => d.y))) ?? 0,
    [lines]
  );

  const yScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, yMax || 1])
        .nice()
        .range([innerHeight, 0]),
    [yMax, innerHeight]
  );

  // Offset overlapping lines vertically so both are visible
  const offsets = useMemo(() => {
    if (lines.length < 2) return lines.map(() => 0);
    // Check if lines have identical data
    const a = lines[0]?.data;
    const b = lines[1]?.data;
    const identical =
      a && b && a.length === b.length && a.every((pt, i) => pt.y === b[i].y);
    return identical ? [-3, 3] : lines.map(() => 0);
  }, [lines]);

  const linePaths = useMemo(() => {
    return lines.map((l, index) => {
      const offset = offsets[index] ?? 0;
      const lineGenerator = d3
        .line<{ x: string; y: number }>()
        .x((d) => xScale(d.x) ?? 0)
        .y((d) => yScale(d.y) + offset);

      return {
        ...l,
        d: lineGenerator(l.data) ?? '',
        offset,
      };
    });
  }, [lines, xScale, yScale, offsets]);

  const yTicks = yScale.ticks(4);

  return (
    <div className="w-full" data-testid="med-line-chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        role="img"
        aria-label="Line chart"
      >
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          {/* Y-axis grid lines and labels */}
          {yTicks.map((tick) => (
            <g key={tick} transform={`translate(0, ${yScale(tick)})`}>
              <line
                x1={0}
                x2={innerWidth}
                className="stroke-neutral-200"
                strokeDasharray="2,2"
              />
              <text
                x={-8}
                textAnchor="end"
                dominantBaseline="middle"
                className="fill-neutral-500 text-[10px]"
              >
                {formatY(tick)}
              </text>
            </g>
          ))}

          {/* X-axis labels */}
          {xLabels.map((label) => (
            <text
              key={label}
              x={xScale(label)}
              y={innerHeight + 16}
              textAnchor="middle"
              className="fill-neutral-500 text-[10px]"
            >
              {label}
            </text>
          ))}

          {/* Lines */}
          {linePaths.map((line) => (
            <path
              key={line.label}
              d={line.d}
              fill="none"
              stroke={line.color}
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
              data-testid={`line-${line.label}`}
            />
          ))}

          {/* Data point dots */}
          {linePaths.map((line, lineIndex) =>
            line.data.map((point) => (
              <circle
                key={`${line.label}-${point.x}`}
                cx={xScale(point.x)}
                cy={yScale(point.y) + (offsets[lineIndex] ?? 0)}
                r={5}
                fill={lineIndex === 0 ? line.color : 'white'}
                stroke={line.color}
                strokeWidth={lineIndex === 0 ? 2 : 3}
                data-testid={`dot-${line.label}-${point.x}`}
              />
            ))
          )}
        </g>
      </svg>
    </div>
  );
};

export default MedLineChart;
